import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Pill, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import "./mail-settings.css";

/**
 * Whether mail works, and proof either way.
 *
 * Read-only on purpose. The credentials live in the environment, following S3
 * and the push keys: a password saved through a web form is one nobody
 * reviews, and a wrong value should stop a deployment rather than fail
 * silently at seven in the morning when the first digest goes out.
 *
 * So this screen's job is not configuration — it is telling an administrator
 * what the server is set to, letting them prove it, and showing what has
 * actually been sent.
 */

type MailStatus = {
  configured: boolean;
  host: string | null;
  port: number;
  secure: boolean;
  from: string | null;
  fromName: string;
  authenticated: boolean;
  recent: {
    id: string;
    recipient: string;
    subject: string;
    kind: string;
    status: string;
    detail: string | null;
    createdAt: string;
  }[];
};

const STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  sent: "ok",
  skipped: "neutral",
  failed: "danger",
};

export function MailSettings() {
  const { toast } = useToast();
  const [status, setStatus] = useState<MailStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<MailStatus>("/admin/mail")
      .then(setStatus)
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not read the mail settings.", { tone: "danger" });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => load(), [load]);

  const sendTest = useCallback(() => {
    setTesting(true);
    api
      .post<{ status: string; stage: string; sentTo?: string; detail?: string }>("/admin/mail/test", {})
      .then((result) => {
        if (result.status === "sent") toast(`Test message sent to ${result.sentTo}.`);
        else toast(result.detail ?? "The test did not send.", { tone: "danger" });
        // Either way it is now in the log, which is the point of having one.
        load();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not send a test.", { tone: "danger" });
      })
      .finally(() => setTesting(false));
  }, [load, toast]);

  if (loading && !status) return <SkeletonText lines={5} />;
  if (!status) return null;

  return (
    <div className="mail-settings">
      <div className="mail-settings__head">
        <h3>Outbound mail</h3>
        <Pill tone={status.configured ? "ok" : "neutral"} dot>
          {status.configured ? "Configured" : "Not configured"}
        </Pill>
      </div>

      {status.configured ? (
        <>
          <dl className="mail-settings__facts">
            <div>
              <dt>Server</dt>
              <dd>
                {status.host}:{status.port} {status.secure ? "(TLS)" : "(STARTTLS)"}
              </dd>
            </div>
            <div>
              <dt>From</dt>
              <dd>
                {status.fromName} &lt;{status.from}&gt;
              </dd>
            </div>
            <div>
              <dt>Authentication</dt>
              <dd>{status.authenticated ? "Username and password" : "None"}</dd>
            </div>
          </dl>

          <div className="mail-settings__actions">
            <Button size="sm" variant="secondary" onClick={sendTest} loading={testing}>
              Send a test to myself
            </Button>
            <span className="mail-settings__note">
              Goes to your own address only — this is a check, not a way to mail somebody.
            </span>
          </div>
        </>
      ) : (
        /*
         * Not an error state. A fresh install has no mail, and the settings
         * live in the environment, so this says where to put them rather than
         * offering a form that would not be read.
         */
        <div className="mail-settings__unset">
          <p>
            No mail server is set, so invitations must be sent by copying their link, and no reminders go out.
          </p>
          <p className="mail-settings__note">
            Set <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USER</code>, <code>SMTP_PASSWORD</code> and{" "}
            <code>SMTP_FROM</code> in the deployment environment, then restart the API. Credentials belong there rather
            than in this screen, so a wrong value stops a deployment instead of failing quietly later.
          </p>
        </div>
      )}

      <h4 className="mail-settings__log-title">Recent mail</h4>
      {status.recent.length === 0 ? (
        <EmptyState
          icon="document"
          title="Nothing sent yet"
          description="Invitations, tests and digests will be listed here with what happened to them."
          size="inline"
        />
      ) : (
        <ul className="mail-settings__log">
          {status.recent.map((row) => (
            <li key={row.id}>
              <Pill tone={STATUS_TONE[row.status] ?? "neutral"}>{row.status}</Pill>
              <span className="mail-settings__log-to">{row.recipient}</span>
              <span className="mail-settings__log-subject">{row.subject}</span>
              {/* Why it failed, beside the failure. A status with no reason
                  sends somebody to the server logs to find out. */}
              {row.detail && <span className="mail-settings__log-detail">{row.detail}</span>}
              <span className="mail-settings__log-when">
                {new Date(row.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
